/**
 * #547 — an MQTT `insertOnlyIfMissing` ack used to mean "the message was received", not "the send
 * thunk started": the handler fire-and-forgot `retrySendMessageThunk` and replied with a CACHED
 * whatsappState. A stall before the thunk's first client log row left the server with an ack and
 * zero client attempt rows (`retries_exhausted_no_attempt`). These helpers give the handler a
 * start signal to wait on, and give the thunk a named-reason bound for its pre-log awaits.
 */

/** Pre-log awaits in the retry thunk are bounded by this; a hang becomes a named, logged failure. */
export const RETRY_PRE_LOG_TIMEOUT_MS = 10_000;

/** How long the handler waits for the thunk to start before acking anyway (never blocks the reply). */
export const RETRY_START_WAIT_MS = 3_000;

/** Client log row written as the thunk's very first action. Must NOT match BE SEND_ATTEMPT_LOG_RE. */
export const RETRY_THUNK_ENTERED_LOG = (trigger: string | undefined): string =>
  `Retry thunk entered (${trigger ?? 'unknown'} trigger)`;

const waiters = new Map<string, Array<() => void>>();

/** Register interest in a message's thunk start. Call BEFORE dispatching the thunk. */
export function waitForRetryThunkStart(messageId: string, timeoutMs: number = RETRY_START_WAIT_MS): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const done = (started: boolean): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      const list = waiters.get(messageId)?.filter((w) => w !== onStart) ?? [];
      if (list.length) waiters.set(messageId, list);
      else waiters.delete(messageId);
      resolve(started);
    };
    const onStart = (): void => done(true);
    const timer = setTimeout(() => done(false), timeoutMs);
    waiters.set(messageId, [...(waiters.get(messageId) ?? []), onStart]);
  });
}

/** Called synchronously on thunk entry, before any await. */
export function markRetryThunkStarted(messageId: string | undefined): void {
  if (!messageId) return;
  const list = waiters.get(messageId);
  if (!list) return;
  waiters.delete(messageId);
  list.forEach((w) => w());
}

/** Bound an await with a named reason; the rejection message carries `name` and the limit. */
export function boundedStep<T>(step: Promise<T>, name: string, ms: number = RETRY_PRE_LOG_TIMEOUT_MS): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`retry pre-log step "${name}" timed out after ${ms} ms`)), ms);
  });
  return Promise.race([step, timeout]).finally(() => clearTimeout(timer));
}
